import type { CodeBlockRedlineItem } from '@aline/common';
import { getReviewableSuggestions, sortPlaybookRulePositionsByRank } from '@aline/common';
import { Apollo } from 'src/gql/generated';

import type { ParsedProposal } from './playbook-run-hooks';
import { parseProposal } from './playbook-run-hooks';
import type {
  PlaybookPositionResult,
  PlaybookProposal,
  PlaybookRuleJob,
  PlaybookRuleJobSummary,
  PositionActionLocks,
} from './playbook-run-state';
import {
  getEffectiveDecisions,
  getIsPositionProposalStale,
  getIsRuleReviewable,
  getPlaybookPositionLabel,
  getPositionActionLocks,
  getReviewingPositionId,
  getSortedResults,
} from './playbook-run-state';
import { PLAYBOOK_CHIP_LABELS } from './PlaybookChip';
import type { PlaybookChipTone } from './PlaybookChip';

export type PlaybookLivePosition = { id: string; rank: string; ruleText: string };

export type PlaybookPositionChip = {
  label: string;
  tone: PlaybookChipTone;
};

export type PlaybookTemplatePositionBlock = {
  label: string;
  positionId: string;
  source: 'template';
  text: string;
};

export type PlaybookRunPositionBlockView = {
  isFallback: boolean;
  label: string;
  positionId: string;
  result: PlaybookPositionResult;
  source: 'run';
  text: string;
};

export type PlaybookPositionBlockView =
  | PlaybookRunPositionBlockView
  | PlaybookTemplatePositionBlock;

/**
 * What the rule card lists as position blocks. A job — finished, in flight, or
 * failed — is the snapshot: live template edits do not appear until the next
 * run. With no job, the live template is a preview.
 */
export function getPlaybookRulePositionBlocks(options: {
  job: PlaybookRuleJobSummary | null;
  livePositions: readonly PlaybookLivePosition[];
}): PlaybookPositionBlockView[] {
  const { job, livePositions } = options;
  if (!job)
    return sortPlaybookRulePositionsByRank(livePositions).map((position, index) => ({
      label: getPlaybookPositionLabel(index),
      positionId: position.id,
      source: 'template',
      text: position.ruleText,
    }));

  return getSortedResults(job).map((result, index) => ({
    isFallback: index > 0,
    label: getPlaybookPositionLabel(index),
    positionId: result.positionId,
    result,
    source: 'run',
    text: result.ruleText,
  }));
}

/**
 * The card body is the positions (and a failed-run banner). A job still
 * running, or one that failed with no reason yet, has neither — keep the
 * title row tight against the next hairline instead of padding an empty box.
 */
export function getHasPlaybookRuleCardBody(options: {
  failedReason: string | null | undefined;
  positionBlockCount: number;
}): boolean {
  return options.positionBlockCount > 0 || Boolean(options.failedReason);
}

export function getParsedProposalsByPositionId(
  proposals: readonly PlaybookProposal[],
  pendingAcceptedSuggestionIds: ReadonlySet<string>
): Map<string, ParsedProposal> {
  return new Map(
    proposals.flatMap(stored => {
      const parsed = parseProposal(stored);
      if (!parsed) return [];

      return [
        [
          stored.positionId,
          {
            decisions: getEffectiveDecisions(
              parsed.suggestions,
              parsed.decisions,
              pendingAcceptedSuggestionIds
            ),
            suggestions: parsed.suggestions,
          },
        ],
      ];
    })
  );
}

export function getReviewingPositionIdFromProposals(
  parsedProposalByPositionId: Map<string, ParsedProposal>,
  isResolved: boolean
): string | null {
  return getReviewingPositionId(
    [...parsedProposalByPositionId.entries()].map(([positionId, proposal]) => ({
      decisions: proposal.decisions,
      positionId,
    })),
    isResolved
  );
}

/**
 * Once any action is active, exactly one position carries color and everything
 * else mutes, so the eye goes only to the resolution. Reviewing sits between
 * Failed and Resolved: review is underway here.
 */
export function getPositionOutcomeChip(options: {
  hasAcceptedSuggestion: boolean;
  resolution: PlaybookRuleJobSummary['resolution'];
  result: PlaybookPositionResult;
}): PlaybookPositionChip {
  const { hasAcceptedSuggestion, resolution, result } = options;
  if (resolution && resolution.positionId === result.positionId) {
    const ownChip = getOwnResolutionChip(resolution.kind, hasAcceptedSuggestion);
    if (ownChip) return ownChip;
  }

  const muted = Boolean(resolution);
  if (result.status === Apollo.PlaybookPositionStatus.Passing)
    return { label: PLAYBOOK_CHIP_LABELS.met, tone: muted ? 'muted-passed' : 'passed' };
  if (result.failedAt)
    return {
      label: PLAYBOOK_CHIP_LABELS.couldntEvaluate,
      tone: muted ? 'muted-failed' : 'failed',
    };
  return { label: PLAYBOOK_CHIP_LABELS.notMet, tone: muted ? 'muted-failed' : 'failed' };
}

export type RunPositionPresentation = {
  chip: PlaybookPositionChip;
  dimmed: boolean;
  explanation: string | null;
  isProposalStale: boolean;
  locks: PositionActionLocks;
  othersDisabled: boolean;
  ownFallbackActive: boolean;
  showFallbackAccept: boolean;
  showSuggestionReview: boolean;
};

export function getRunPositionPresentation(options: {
  getIsStale: (previewCards: CodeBlockRedlineItem[]) => boolean;
  isFallback: boolean;
  parsedProposal: ParsedProposal | null;
  resolution: PlaybookRuleJobSummary['resolution'];
  result: PlaybookPositionResult;
  reviewingPositionId: string | null;
}): RunPositionPresentation {
  const { getIsStale, isFallback, parsedProposal, resolution, result, reviewingPositionId } =
    options;
  const isResolved = Boolean(resolution);
  const isFailing = result.status === Apollo.PlaybookPositionStatus.Failing;
  const isPassing = result.status === Apollo.PlaybookPositionStatus.Passing;
  const isOwn = resolution?.positionId === result.positionId;
  const suggestActive = Boolean(
    isOwn && resolution.kind === Apollo.PlaybookRuleResolutionKind.Suggest
  );
  const reviewedActive = Boolean(
    isOwn && resolution.kind === Apollo.PlaybookRuleResolutionKind.Reviewed
  );
  const ownFallbackActive = Boolean(
    isOwn && resolution.kind === Apollo.PlaybookRuleResolutionKind.Fallback
  );
  const isSiblingReviewing =
    reviewingPositionId !== null && reviewingPositionId !== result.positionId;
  const isUnusedAfterResolution = isResolved && resolution?.positionId !== result.positionId;
  const undecidedSuggestions = parsedProposal
    ? getReviewableSuggestions(parsedProposal.suggestions).filter(
        suggestion => parsedProposal.decisions[suggestion.id] === undefined
      )
    : [];
  const isProposalStale = parsedProposal
    ? getIsPositionProposalStale({
        decisions: parsedProposal.decisions,
        getIsStale,
        isResolved,
        suggestions: parsedProposal.suggestions,
      })
    : false;
  const hasAcceptedSuggestion = Object.values(parsedProposal?.decisions ?? {}).some(
    decision => decision === 'accepted'
  );

  return {
    chip: getPositionOutcomeChip({
      hasAcceptedSuggestion,
      resolution,
      result,
    }),
    dimmed: isUnusedAfterResolution,
    explanation: result.explanation || result.failedReason,
    isProposalStale,
    locks: getPositionActionLocks({
      hasResolution: isResolved,
      hasUndecided: undecidedSuggestions.length > 0,
      isProposalStale,
      isSiblingReviewing,
      reviewedActive,
    }),
    othersDisabled:
      (isResolved && !(suggestActive || reviewedActive || ownFallbackActive)) || isSiblingReviewing,
    ownFallbackActive,
    showFallbackAccept: isPassing && isFallback && !isUnusedAfterResolution,
    showSuggestionReview: isFailing && Boolean(parsedProposal) && !isResolved,
  };
}

/**
 * A resolve control for the rule as a whole, below its positions:
 * - `mark-resolved`: the rule is open for review and its positions offer no
 *   other way to resolve it: no edit to accept or reject (AI notes have no
 *   decision) and no passing fallback to accept. A rule that offers either
 *   resolves through it, and Skip rule stays available to close it without one.
 * - `resolved`: the rule carries a `Manual` resolution, from that button or
 *   from Skip rule, which writes the same record. The notice is how either
 *   one is undone.
 */
export type RuleResolveAction = 'mark-resolved' | 'resolved';

export function getRuleResolveAction(job: PlaybookRuleJob | null): RuleResolveAction | null {
  if (job?.resolution?.kind === Apollo.PlaybookRuleResolutionKind.Manual) return 'resolved';
  if (!job || job.resolution || !getIsRuleReviewable(job)) return null;

  const hasReviewableSuggestion = job.proposals.some(proposal => {
    const parsed = parseProposal(proposal);
    return Boolean(parsed && getReviewableSuggestions(parsed.suggestions).length);
  });
  const hasPassingPosition = job.positionResults.some(
    result => result.status === Apollo.PlaybookPositionStatus.Passing
  );
  if (hasReviewableSuggestion || hasPassingPosition) return null;
  return 'mark-resolved';
}

function getOwnResolutionChip(
  kind: Apollo.PlaybookRuleResolutionKind,
  hasAcceptedSuggestion: boolean
): PlaybookPositionChip | null {
  const chips = {
    [Apollo.PlaybookRuleResolutionKind.Fallback]: {
      label: PLAYBOOK_CHIP_LABELS.accepted,
      tone: 'resolved',
    },
    [Apollo.PlaybookRuleResolutionKind.Manual]: null,
    [Apollo.PlaybookRuleResolutionKind.Reviewed]: {
      label: hasAcceptedSuggestion
        ? PLAYBOOK_CHIP_LABELS.editsApplied
        : PLAYBOOK_CHIP_LABELS.reviewed,
      tone: 'resolved',
    },
    [Apollo.PlaybookRuleResolutionKind.Suggest]: {
      label: PLAYBOOK_CHIP_LABELS.editsAdded,
      tone: 'resolved',
    },
  } as const satisfies Record<Apollo.PlaybookRuleResolutionKind, PlaybookPositionChip | null>;

  return chips[kind];
}
